/**
 * PaymentPendingPage.jsx — /payment/pending
 *
 * Shown when a student logs in but has no completed payment.
 * Gives them three options:
 *   1. Pay now (re-initializes Paystack)
 *   2. Call support
 *   3. Log out
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/apiClient';

const DEFAULTS = { A1:3550, A2:3999, B1:4200, B2:4500 };

export default function PaymentPendingPage() {
  const { user, logout, refreshPaymentStatus } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const level = user?.student?.currentLevel || user?.currentLevel || 'A1';
  const fee   = DEFAULTS[level] || 3550;

  const handlePay = async () => {
    setLoading(true);
    setError('');
    try {
      // Check if there's already a pending payment record for this student
      const studentId = user?.studentId || user?.student?.id;
      if (!studentId) {
        setError('Could not find your student account. Please contact support.');
        return;
      }

      const { data } = await API.post('/payments/initialize', {
        studentId,
        level,
        email: user.email,
      });

      if (data.authorizationUrl) {
        window.location.href = data.authorizationUrl;
      } else {
        setError('Could not start payment. Please try again or contact support.');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Payment initialization failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // If payment was completed in another tab, refresh and redirect
  const handleRefresh = async () => {
    setLoading(true);
    await refreshPaymentStatus();
    setLoading(false);
    // AuthContext will redirect automatically once hasPaid flips to true
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F5F5F5' }}>
      <div className="w-full max-w-md">

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div style={{ height: 4, background: '#DD0000' }} />

          <div className="p-8 text-center">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5"
              style={{ background: '#fff8f0' }}>
              <span className="text-4xl">💳</span>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Required</h2>
            <p className="text-gray-500 text-sm mb-6">
              Hi <strong>{user?.name}</strong>! Your account is created but you need to
              complete your course fee payment to access the platform.
            </p>

            {/* Course details */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Level</span>
                <span className="font-bold" style={{ color: '#DD0000' }}>{level}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Course Fee</span>
                <span className="font-bold text-gray-900">
                  GHS {fee.toLocaleString('en-GH', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Payment Methods</span>
                <span className="text-gray-700 text-xs">MTN MoMo · Vodafone · Card · Bank</span>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Pay now */}
            <button
              onClick={handlePay}
              disabled={loading}
              className="w-full py-3.5 rounded-xl font-bold text-sm text-white mb-3 disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: loading ? '#999' : '#DD0000' }}>
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3"
                      strokeDasharray="40 20" />
                  </svg>
                  Processing…
                </>
              ) : (
                `🔒 Pay GHS ${fee.toLocaleString('en-GH', { minimumFractionDigits: 2 })} Now`
              )}
            </button>

            {/* Already paid in another tab */}
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="w-full py-2.5 rounded-xl font-semibold text-sm text-gray-600 border-2 border-gray-200 hover:border-gray-300 mb-4 disabled:opacity-60">
              ↻ I already paid — refresh my status
            </button>

            <div className="pt-4 border-t border-gray-100 space-y-2">
              <p className="text-sm text-gray-600">
                Need help?{' '}
                <a href="tel:0202071417"
                  className="font-bold" style={{ color: '#DD0000' }}>
                  0202071417
                </a>
              </p>
              <button
                onClick={handleLogout}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
                Sign out
              </button>
            </div>
          </div>
        </div>

        <p className="text-xs text-gray-400 text-center mt-4">
          🔒 Payments secured by Paystack · DeutschBridge Institute
        </p>
      </div>
    </div>
  );
}
